import React from 'react';
import { SharedFreeTrial } from '@prerender/prerender-shared/FE';

import { useSelector } from '../../hooks/usePrerenderUser';
import useFreeTrialState from './useFreeTrialState';
import useFeatureFlag, { FeatureFlag } from '../../hooks/useFeatureFlag';

const TRIAL_DURATION_DAYS = 30; // Define the trial duration in days

// This is a custom hook that combines the functionality of DynamicStateService and Redux as a single source of truth
export const useTrial = () => {
  const [freeTrialState, updateFreeTrialState] = useFreeTrialState();

  const hadTrialBefore = useSelector((state) => state.prerenderUser.hadTrialBefore);
  const isInTrial = useSelector((state) => state.prerenderUser.isTrial);
  const trialEndsAt = useSelector((state) => state.prerenderUser.trialEndsAt);
  const has2025PricingPlans = useFeatureFlag([FeatureFlag.Pricing2025]);

  const daysLeftOnTrial = React.useMemo(() => {
    if (!trialEndsAt) return null; // it's important to know if the trial has ever started

    const now = new Date();
    const diffTime = trialEndsAt.getTime() - now.getTime();
    const diffDays = +(diffTime / (1000 * 60 * 60 * 24)).toFixed(2);
    return Math.max(diffDays, 0); // Ensure we don't return negative days
  }, [trialEndsAt]);

  const isHalfwayThroughTrial = React.useMemo(() => {
    if (!trialEndsAt) return false;

    const now = new Date();

    const trialStartDate = new Date(trialEndsAt);
    trialStartDate.setDate(trialStartDate.getDate() - TRIAL_DURATION_DAYS);

    const halfwayPoint = new Date(trialEndsAt);
    halfwayPoint.setDate(halfwayPoint.getDate() - TRIAL_DURATION_DAYS / 2);

    return now >= halfwayPoint && now <= trialEndsAt;
  }, [trialEndsAt]);

  const discountAvailable = React.useMemo(() => {
    const now = new Date();
    const { discountAvailableUntil } = freeTrialState.company;
    if (!discountAvailableUntil || now > discountAvailableUntil) return;

    return SharedFreeTrial.Coupons.DefaultDiscount;
  }, [freeTrialState.company.discountAvailableUntil]);

  const hasTrialEnded = daysLeftOnTrial === 0 && !isInTrial;
  const isLegacyTrial = !has2025PricingPlans;

  return {
    updateFreeTrialState,
    freeTrialState,
    isInTrial: !!isInTrial,
    trialEndsAt,
    daysLeftOnTrial,
    hasTrialEnded,
    isHalfwayThroughTrial,
    hadTrialBefore,
    isLegacyTrial, // it was decided to ship both versions of free trial at the same time, so this differentiate between people on the old vs people on the new
    discountAvailable,
  };
};
